import { getAtPath, setAtPath } from './configPaths'
import { isSliderV3 } from './sliderValidation'

/**
 * Word hyphenation for ad copy.
 *
 * Ads must break long words (German compounds above all) with a visible "-" instead of
 * chopping them anywhere. CSS `hyphens: auto` alone is not reliable — Chrome downloads
 * hyphenation dictionaries lazily and headless/kiosk builds may never have them — so the
 * editor inserts soft hyphens (U+00AD) itself with Liang's algorithm and the TeX patterns
 * from the `hyphen` package. Every browser honours soft hyphens; the templates render the
 * break with `hyphenate-character: "-"`.
 *
 * The form state stays clean: soft hyphens are added on the way to the preview and to the
 * saved revision, and stripped again whenever a configuration is loaded back.
 */
const SOFT_HYPHEN = '\u00AD'
const SOFT_HYPHEN_PATTERN = /\u00AD/g
const MIN_WORD_LENGTH = 7
const HYPHENATED_TYPES = new Set(['rich-text', 'textarea'])

const LOADERS = {
  de: () => import(/* webpackChunkName: "hyphen-de" */ 'hyphen/de'),
  'en-gb': () => import(/* webpackChunkName: "hyphen-en-gb" */ 'hyphen/en-gb'),
  en: () => import(/* webpackChunkName: "hyphen-en" */ 'hyphen/en-us'),
}

/** Pattern set for an ad language code (`de`, `de-DE`, `en`, `en-US`, `gb`, `en-GB`, …). */
export function hyphenationLanguage(code) {
  const raw = String(code || '').trim().toLowerCase()
  if (!raw) return null
  if (raw === 'gb' || raw === 'en-gb') return 'en-gb'
  const base = raw.split(/[-_]/)[0]
  if (base === 'de') return 'de'
  if (base === 'en') return 'en'
  return null
}

const cache = new Map()

/** Resolves to `{ language, text(s), html(s) }` or null when the language has no patterns. */
export function loadHyphenator(code) {
  const language = hyphenationLanguage(code)
  if (!language) return Promise.resolve(null)
  if (!cache.has(language)) {
    cache.set(language, LOADERS[language]().then((module) => {
      const api = module && module.hyphenateSync ? module : module.default
      const options = { hyphenChar: SOFT_HYPHEN, minWordLength: MIN_WORD_LENGTH }
      return {
        language,
        text: (value) => api.hyphenateSync(value, options),
        html: (value) => api.hyphenateHTMLSync(value, options),
      }
    }))
  }
  return cache.get(language)
}

export function stripSoftHyphens(value) {
  // Slider 3 accepts semantic compound seams from authors and the creative assistant.
  if (isSliderV3(value)) return value
  if (typeof value === 'string') {
    return value.includes(SOFT_HYPHEN) ? value.replace(SOFT_HYPHEN_PATTERN, '') : value
  }
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((entry) => {
      const stripped = stripSoftHyphens(entry)
      if (stripped !== entry) changed = true
      return stripped
    })
    return changed ? next : value
  }
  if (value && typeof value === 'object') {
    let changed = false
    const next = {}
    Object.keys(value).forEach((key) => {
      const stripped = stripSoftHyphens(value[key])
      if (stripped !== value[key]) changed = true
      next[key] = stripped
    })
    return changed ? next : value
  }
  return value
}

/**
 * Returns a copy of `configuration` whose rich-text and textarea fields (declared by the
 * schema, top-level and inside collections) carry soft hyphens. Short single-line fields
 * (names, CTA labels, prices) are left alone — they never wrap.
 */
export function hyphenateConfiguration(schema, configuration, hyphenator) {
  if (!schema || !configuration || !hyphenator) return configuration
  let next = configuration
  const slider = isSliderV3(configuration)

  const sliderText = (value) => String(value).split(/(<[^>]*>)/g).map((part) => {
    if (part.startsWith('<')) return part
    return part.replace(/[\p{L}\u00ad]+/gu, (word) => {
      if (word.includes(SOFT_HYPHEN) || word.length < 10) return word
      let position = 0
      return hyphenator.text(word).split(SOFT_HYPHEN).map((segment, index, all) => {
        position += segment.length
        return segment + (index < all.length - 1 && position >= 4 && word.length - position >= 4 ? SOFT_HYPHEN : '')
      }).join('')
    })
  }).join('')

  const apply = (field, path) => {
    if (slider && field?.type === 'string-list' && path.endsWith('.elements.bullets.items')) {
      const items = getAtPath(next, path)
      if (Array.isArray(items)) next = setAtPath(next, path, items.map(sliderText))
      return
    }
    // Single-line fields (button labels) opt out even when they are rich text.
    if (!field || !HYPHENATED_TYPES.has(field.type) || field.hyphenate === false) return
    const value = getAtPath(next, path)
    if (typeof value !== 'string' || !value.trim()) return
    const clean = value.replace(SOFT_HYPHEN_PATTERN, '')
    let output
    try {
      output = slider ? sliderText(value) : field.type === 'rich-text' ? hyphenator.html(clean) : hyphenator.text(clean)
    } catch {
      return
    }
    if (typeof output === 'string' && output !== value) next = setAtPath(next, path, output)
  }

  for (const section of schema.sections || []) {
    if (section.collection) continue
    for (const field of section.fields || []) apply(field, field.path)
  }
  for (const collection of schema.collections || []) {
    if (!collection?.path) continue
    const items = getAtPath(next, collection.path)
    if (!Array.isArray(items)) continue
    items.forEach((item, index) => {
      for (const itemSection of collection.itemSections || []) {
        for (const field of itemSection.fields || []) {
          apply(field, `${collection.path}.${index}.${field.path}`)
        }
      }
    })
  }
  return next
}
